import { useEffect, useState, useCallback } from "react";
import { Input, Table, Select, message, Button } from "antd";
import { Link } from "react-router-dom";
import moment from "moment";
import EditUserModal from './EditUserModal';
import { fetchUsersByRoleIdForBranch } from "../apis/rolesApi";
import Roles from "../enums/Roles";
import { fetchUser } from "../apis/usersApi";

const UserList = ({
  users = [],
  total = 0,
  loading = false,
  currentPage = 1,
  pageSize = 50,
  onTableChange,
  showActivationDate = false,
  showExpirationDate = false,
  showRemainingClasses = false,
}) => {
  const [followers, setFollowers] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [searchedId, setSearchedId] = useState("");
  const [searchedName, setSearchedName] = useState("");
  const [searchedPhone, setSearchedPhone] = useState("");
  const [searchedEmail, setSearchedEmail] = useState("");
  const [selectedFollower, setSelectedFollower] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");

  // Fetch followers
  useEffect(() => {
    const loadFollowers = async () => {
      try{
        const branch_id = localStorage.getItem('branch_id');
        const response = await fetchUsersByRoleIdForBranch(Roles.CLIENT_FOLLOWER, branch_id);
        setFollowers(response);
      } catch (error) {
        console.error('UserList - loadFollowers, Error fetching client followers:', error);
        message.error("Error al cargar las madrinas. Contacta al soporte");
        setFollowers([]);
      }
    };
    loadFollowers();
  }, []);

  const notifyChange = (filters) => {
    if (onTableChange) {
      onTableChange({
        page: 1, // Reset to 1 on filter
        search_id: filters.id !== undefined ? filters.id : searchedId,
        search_name: filters.name !== undefined ? filters.name : searchedName,
        search_phone: filters.phone !== undefined ? filters.phone : searchedPhone,
        search_email: filters.email !== undefined ? filters.email : searchedEmail,
        follower_id: filters.follower !== undefined ? filters.follower : selectedFollower,
        plan_name: filters.plan !== undefined ? filters.plan : selectedPlan,
      });
    }
  };

  const handleIdChange = (e) => {
    const val = e.target.value;
    setSearchedId(val);
    notifyChange({ id: val });
  };

  const handleNameChange = (e) => {
    const val = e.target.value;
    setSearchedName(val);
    notifyChange({ name: val });
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value;
    setSearchedPhone(val);
    notifyChange({ phone: val });
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setSearchedEmail(val);
    notifyChange({ email: val });
  };

  const handleFollowerChange = (value) => {
    const val = value || "";
    setSelectedFollower(val);
    notifyChange({ follower: val });
  };

  const handlePlanChange = (value) => {
    const val = value || "";
    setSelectedPlan(val);
    notifyChange({ plan: val });
  };

  const handleAntTableChange = (pagination, filters, sorter) => {
    if (onTableChange) {
      onTableChange({
        page: pagination.current,
        sort_field: sorter.field,
        sort_order: sorter.order === 'ascend' ? 'asc' : (sorter.order === 'descend' ? 'desc' : undefined),
        search_id: searchedId,
        search_name: searchedName,
        search_phone: searchedPhone,
        search_email: searchedEmail,
        follower_id: selectedFollower,
        plan_name: selectedPlan,
      });
    }
  };

  const onEditClick = async (user) => {
    const fullUser = await fetchUser(user.id);
    setSelectedUser(fullUser);
    setModalVisible(true);
  }

  const columns = [
    {
      title: (
        <div>
          <p>Id</p>
          <Input placeholder="Buscar id" value={searchedId} onChange={handleIdChange} />
        </div>
      ),
      dataIndex: "id",
      key: "id",
      align: "center",
      onHeaderCell: () => ({
        style: { minWidth: 100 }
      }),
    },
    {
      title: (
        <div>
          <p>Nombre</p>
          <Input placeholder="Buscar Nombre" value={searchedName} onChange={handleNameChange} />
        </div>
      ),
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Link to={`/users/${record.id}`}>
          {`${record.name} ${record.surname_1}`}
        </Link>
      ),
      align: "center",
      onHeaderCell: () => ({
        style: { minWidth: 100 }
      }),
    },
    {
      title: (
        <div>
          <p>Celular</p>
          <Input placeholder="Buscar celular" value={searchedPhone} onChange={handlePhoneChange} />
        </div>
      ),
      dataIndex: "phone",
      key: "phone",
      align: "center",
      onHeaderCell: () => ({
        style: { minWidth: 100 }
      }),
    },
    {
      title: (
        <div>
          <p>Email</p>
          <Input placeholder="Buscar email" value={searchedEmail} onChange={handleEmailChange} />
        </div>
      ),
      dataIndex: "email",
      key: "email",
      align: "center",
      onHeaderCell: () => ({
        style: { minWidth: 100 }
      }),
    },
    {
      title: (
        <div>
          <p>Plan</p>
          <Input placeholder="Buscar plan" value={selectedPlan} onChange={(e) => handlePlanChange(e.target.value)} />
        </div>
      ),
      dataIndex: "plan_name",
      key: "plan_name",
      align: "center",
    },
    showActivationDate && {
      title: "F. Inicio",
      dataIndex: "activation_date",
      key: "activation_date",
      align: "center",
      render: (text) => <span>{moment(text).format("DD-MM-YYYY")}</span>,
    },
    showExpirationDate && {
      title: "F. Expiración",
      dataIndex: "expiration_date",
      key: "expiration_date",
      align: "center",
      sorter: true,
      render: (text) => <span>{moment(text).format("DD-MM-YYYY")}</span>,
    },
    showRemainingClasses && {
      title: "Clases Restantes",
      dataIndex: "remaining_shared_classes",
      key: "remaining_shared_classes",
      align: "center",
    },
    {
      title: "Última Clase",
      dataIndex: "last_session_date",
      key: "last_session_date",
      align: "center",
      sorter: true,
      render: (text) => <span>{moment(text).format("DD-MM-YYYY")}</span>,
    },
    {
      title: (
        <div>
          <p>Madrina</p>
          <Select
            allowClear
            placeholder="Filtrar madrina"
            style={{ width: "100%" }}
            value={selectedFollower || undefined}
            onChange={handleFollowerChange}
            options={followers.map((f) => ({ value: f.id, label: f.name }))}
          />
        </div>
      ),
      dataIndex: "follower_name",
      key: "follower_name",
      align: "center",
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (text, record, index) => (
        // TODO: Fetch all user info (to edit eps, instagram, etc), Replace with an Edit icon button
        <Button size="small" onClick={() => {onEditClick(record);}}>
          Editar
        </Button>
      ),
    },
  ].filter(Boolean);

  return (
    <>
      <Table
        dataSource={users.map((item, i) => ({ ...item, key: i }))}
        columns={columns}
        loading={loading}
        pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: false, // Keep it simple without limits changers for now
        }}
        onChange={handleAntTableChange}
        className="text-center table-responsive"
      />
      <EditUserModal
        visible={modalVisible}
        onClose={() => { setModalVisible(false); setSelectedUser(null); }}
        user={selectedUser}
        followers={followers}
        onUpdated={(updatedUser) => {
          // Merge updated fields into existing record, compute follower_name from followers list
          const mergeRecord = (existing) => {
            const merged = { ...existing, ...updatedUser };
            if (merged.assigned_id !== undefined && followers && followers.length) {
              const follower = followers.find((f) => String(f.id) === String(merged.assigned_id));
              merged.follower_name = follower ? follower.name : merged.follower_name || null;
            }
            return merged;
          };

          // Server-side refreshing might be preferred, but this is ok for modal sync
          if(onTableChange) onTableChange({ page: currentPage, forceRefresh: true });
        }}
      />
    </>
  );
};

export default UserList;
